// ABOUTME: Tabbed hub layout + tab configs that consolidate related pages under
// ABOUTME: one menu entry. Each tab deep-links to its existing route (no URL changes).

import { Tabs } from '@boundarylab/ui';
import { Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { RouteLoader } from './RouteLoader';

export interface HubTab {
  id: string;
  label: string;
  path: string;
}

// A layout route: renders the tab bar for its cluster, then the matched child
// page via <Outlet/>. Active tab is derived from the URL so deep-links and menu
// navigation both light up the right tab.
export function HubLayout({ tabs }: { tabs: HubTab[] }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active =
    tabs.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`)) ?? tabs[0];

  return (
    <div>
      <div className="mb-4 border-b border-slate-200">
        <Tabs
          tabs={tabs.map((t) => ({ id: t.id, label: t.label }))}
          activeTab={active.id}
          onChange={(id) => {
            const t = tabs.find((x) => x.id === id);
            if (t) navigate(t.path);
          }}
          variant="underline"
          size="sm"
        />
      </div>
      {/* Own Suspense boundary so a lazy tab page loads BELOW the tab bar
          instead of replacing the whole hub (AuthShell's boundary is above us). */}
      <Suspense fallback={<RouteLoader />}>
        <Outlet />
      </Suspense>
    </div>
  );
}

// All the paths a hub owns — used by the sidebar to highlight the hub's menu
// entry for any of its tabs.
export const DISCOVER_TABS: HubTab[] = [
  { id: 'scanner', label: 'Scanner', path: '/scanner' },
  { id: 'picks', label: 'Picks', path: '/picks' },
  { id: 'entry', label: 'Entry Ranking', path: '/entry-leaderboard' },
  { id: 'discovery', label: 'Discovery', path: '/discovery' },
];

export const TRADE_TABS: HubTab[] = [
  { id: 'queue', label: 'Trade Queue', path: '/trade-queue' },
  { id: 'positions', label: 'Entry Positions', path: '/entry-positions' },
];

export const RISK_TABS: HubTab[] = [
  { id: 'portfolio', label: 'Portfolio', path: '/risk/portfolio' },
  { id: 'asset', label: 'Asset', path: '/risk/asset' },
  { id: 'decomposition', label: 'Decomposition', path: '/risk-decomposition' },
  { id: 'correlation', label: 'Correlation', path: '/correlation' },
  { id: 'factors', label: 'Factors', path: '/factors' },
];

export const STRATEGY_TABS: HubTab[] = [
  { id: 'optimization', label: 'Optimization', path: '/portfolio-optimization' },
  { id: 'backtesting', label: 'Backtesting', path: '/backtesting' },
  { id: 'options', label: 'Options', path: '/options' },
];

export const MARKET_TABS: HubTab[] = [
  { id: 'real-time', label: 'Real-Time', path: '/real-time' },
  { id: 'fundamentals', label: 'Fundamentals', path: '/fundamentals' },
  { id: 'sentiment', label: 'Sentiment', path: '/sentiment' },
];
